// Money — exact arithmetic in minor units (spec §100).
//
// Amounts are stored as decimal numbers in major units (2 decimals for EUR/GBP/USD) for
// compatibility with every existing record, but every calculation converts to integer minor
// units first, so 0.1 + 0.2 never drifts. Rounding: half away from zero, at the currency's
// precision (documented, tested). Different currencies are never added together (§98).

/** ISO 4217 minor-unit exponents that differ from 2 (extend per market pack). */
const MINOR_DIGITS: Record<string, number> = { JPY: 0, KRW: 0, HUF: 2, ISK: 0, CLP: 0, VND: 0, KWD: 3, BHD: 3, OMR: 3, JOD: 3, TND: 3 };
export const minorDigits = (currency?: string | null) => MINOR_DIGITS[(currency ?? 'EUR').toUpperCase()] ?? 2;

/** major → integer minor units, half away from zero */
export function toMinor(amount: number | null | undefined, currency?: string | null): number {
  if (amount === null || amount === undefined || !Number.isFinite(amount)) return 0;
  const f = 10 ** minorDigits(currency);
  // toPrecision(15) removes binary representation noise (1.005 * 100 = 100.49999999999999 → 100.5)
  const r = Math.round(Number((Math.abs(amount) * f).toPrecision(15)));
  return amount < 0 ? -r : r;
}
export const fromMinor = (minor: number, currency?: string | null) => minor / 10 ** minorDigits(currency);
/** round a major amount to the currency's precision */
export const roundMoney = (amount: number, currency?: string | null) => fromMinor(toMinor(amount, currency), currency);
/** exact sum of major amounts in one currency */
export function sumMoney(amounts: readonly (number | null | undefined)[], currency?: string | null): number {
  return fromMinor(amounts.reduce<number>((s, a) => s + toMinor(a ?? 0, currency), 0), currency);
}
/** divide exactly, then round once */
export function divideMoney(amount: number, by: number, currency?: string | null): number {
  if (!by) return 0;
  return fromMinor(Math.round(toMinor(amount, currency) / by), currency);
}
export function multiplyMoney(amount: number, by: number, currency?: string | null): number {
  return fromMinor(Math.round(toMinor(amount, currency) * by), currency);
}

export interface Money { amount: number; currency: string }
/** Totals per currency — never one number for mixed currencies. */
export function sumByCurrency<T>(items: readonly T[], amount: (x: T) => number | null | undefined, currency: (x: T) => string | null | undefined, fallback = 'EUR'): Record<string, number> {
  const minor = new Map<string, number>();
  for (const x of items) {
    const c = (currency(x) || fallback).toUpperCase();
    minor.set(c, (minor.get(c) ?? 0) + toMinor(amount(x) ?? 0, c));
  }
  return Object.fromEntries([...minor].map(([c, m]) => [c, fromMinor(m, c)]));
}
/** one currency → its total; several → null (caller must show the breakdown) */
export function singleCurrencyTotal(totals: Record<string, number>): Money | null {
  const keys = Object.keys(totals);
  if (keys.length !== 1) return keys.length === 0 ? { amount: 0, currency: 'EUR' } : null;
  return { amount: totals[keys[0]!]!, currency: keys[0]! };
}

/** Explicit conversion for reports: requires a rate, its source and its date (§98). */
export interface FxRate { from: string; to: string; rate: number; source: string; date: string }
export function convert(amount: number, fx: FxRate): Money {
  if (!(fx.rate > 0) || !fx.source || !fx.date) throw new Error('fx.rate_incomplete');
  return { amount: multiplyMoney(amount, fx.rate, fx.to), currency: fx.to };
}

// --- payment frequencies -------------------------------------------------------------
export const PER_YEAR = { monthly: 12, quarterly: 4, semiannual: 2, annual: 1, single: 0 } as const;
export type Frequency = keyof typeof PER_YEAR;
export const isRecurring = (f?: string | null) => !!f && f in PER_YEAR && PER_YEAR[f as Frequency] > 0;
